import type { Rect } from '@vp/core';
import { rectContainsRect } from '@vp/core';
import { Camera } from './camera';
import { Canvas2DBackend } from './canvas2d';
import type { FrameInput, GridSettings, ImageProvider, RenderBackend } from './backend';
import type { RenderItem, Scene } from './scene';
import { WebGL2Backend } from './webgl/backend';

export type BackendKind = 'webgl2' | 'canvas2d';

export interface RendererOptions {
  preferred?: BackendKind;
  background?: string;
  grid?: GridSettings | null;
  images?: ImageProvider;
  textLodPx?: number;
  /** Dark canvas: colors inverted with hue kept (dark theme). */
  darkInk?: boolean;
  onBackendChange?: (kind: BackendKind, reason: string) => void;
}

export interface RendererStats {
  backend: BackendKind;
  fps: number;
  frameMs: number;
  frameMsAvg: number;
  visible: number;
  drawCalls: number;
  textures: number;
  textureBytes: number;
  pending: number;
  frames: number;
}

export type OverlayDraw = (ctx: CanvasRenderingContext2D, camera: Camera) => void;

const noImages: ImageProvider = { get: () => null };

/** Owns the canvases, camera, backend selection/fallback and the on-demand frame loop. */
export class Renderer {
  readonly camera = new Camera();
  readonly container: HTMLElement;
  private mainCanvas: HTMLCanvasElement;
  private overlayCanvas: HTMLCanvasElement;
  private overlayCtx: CanvasRenderingContext2D;
  private backend!: RenderBackend;
  private scene: Scene | null = null;
  private raf = 0;
  private dirty = true;
  private sceneDirty = true;
  private items: RenderItem[] = [];
  private queryRect: Rect | null = null;
  private queryZoom = 0;
  private ro: ResizeObserver;
  private unsubScene: (() => void) | null = null;
  private overlayDraw: OverlayDraw | null = null;
  private lastFrameAt = 0;
  private fpsEma = 0;
  private frameMs = 0;
  private frameMsEma = 0;
  private frames = 0;
  private statsListeners = new Set<(s: RendererStats) => void>();
  private disposed = false;
  opts: Required<Omit<RendererOptions, 'onBackendChange'>> & Pick<RendererOptions, 'onBackendChange'>;

  constructor(container: HTMLElement, opts: RendererOptions = {}) {
    this.container = container;
    this.opts = {
      preferred: opts.preferred ?? 'webgl2',
      background: opts.background ?? '#f8fafc',
      grid: opts.grid ?? null,
      images: opts.images ?? noImages,
      textLodPx: opts.textLodPx ?? 4,
      darkInk: opts.darkInk ?? false,
      onBackendChange: opts.onBackendChange,
    };
    if (getComputedStyle(container).position === 'static') container.style.position = 'relative';
    this.mainCanvas = this.makeCanvas('vp-main');
    this.overlayCanvas = this.makeCanvas('vp-overlay');
    this.overlayCanvas.style.pointerEvents = 'none';
    container.append(this.mainCanvas, this.overlayCanvas);
    this.overlayCtx = this.overlayCanvas.getContext('2d')!;
    this.createBackend(this.opts.preferred);
    this.camera.onChange(() => this.invalidate());
    this.ro = new ResizeObserver(() => this.handleResize());
    this.ro.observe(container);
    this.handleResize();
  }

  private makeCanvas(cls: string): HTMLCanvasElement {
    const c = document.createElement('canvas');
    c.className = cls;
    Object.assign(c.style, { position: 'absolute', left: '0', top: '0', width: '100%', height: '100%', display: 'block' });
    return c;
  }

  get backendKind(): BackendKind {
    return this.backend.kind === 'webgl2' ? 'webgl2' : 'canvas2d';
  }

  get canvasElement(): HTMLCanvasElement {
    return this.overlayCanvas;
  }

  private createBackend(kind: BackendKind, reason = 'initial'): void {
    if (kind === 'webgl2') {
      try {
        this.backend = new WebGL2Backend(this.mainCanvas);
        this.opts.onBackendChange?.('webgl2', reason);
        return;
      } catch (e) {
        reason = 'webgl2-unavailable: ' + (e instanceof Error ? e.message : String(e));
      }
    }
    // A canvas that had a WebGL context cannot provide a 2D one → replace the element.
    const fresh = this.makeCanvas('vp-main');
    this.mainCanvas.replaceWith(fresh);
    this.mainCanvas = fresh;
    this.backend = new Canvas2DBackend(this.mainCanvas);
    this.opts.onBackendChange?.('canvas2d', reason);
  }

  /** Switch backend at runtime; data and editor state are untouched (§11.2). */
  setBackend(kind: BackendKind): void {
    if (kind === this.backendKind) return;
    this.backend.dispose();
    if (kind === 'webgl2') {
      const fresh = this.makeCanvas('vp-main');
      this.mainCanvas.replaceWith(fresh);
      this.mainCanvas = fresh;
    }
    this.createBackend(kind, 'user');
    this.handleResize();
  }

  setScene(scene: Scene | null): void {
    this.unsubScene?.();
    this.scene = scene;
    this.unsubScene = scene
      ? scene.onInvalidate(() => {
          this.sceneDirty = true;
          this.invalidate();
        })
      : null;
    this.sceneDirty = true;
    this.invalidate();
  }

  setOverlay(draw: OverlayDraw | null): void {
    this.overlayDraw = draw;
    this.invalidate();
  }

  setGrid(grid: GridSettings | null): void {
    this.opts.grid = grid;
    this.invalidate();
  }

  setBackground(color: string): void {
    this.opts.background = color;
    this.invalidate();
  }

  setDarkInk(on: boolean): void {
    this.opts.darkInk = on;
    this.invalidate();
  }

  setImages(images: ImageProvider): void {
    this.opts.images = images;
    this.invalidate();
  }

  onStats(fn: (s: RendererStats) => void): () => void {
    this.statsListeners.add(fn);
    return () => this.statsListeners.delete(fn);
  }

  invalidate(): void {
    this.dirty = true;
    if (!this.raf && !this.disposed) this.raf = requestAnimationFrame((t) => this.frame(t));
  }

  /** Force the visible set to be re-queried (e.g. after display context change). */
  invalidateScene(): void {
    this.sceneDirty = true;
    this.invalidate();
  }

  private handleResize(): void {
    const r = this.container.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    this.camera.resize(r.width, r.height, dpr);
    this.backend.resize(r.width, r.height, dpr);
    this.overlayCanvas.width = Math.max(1, Math.round(r.width * dpr));
    this.overlayCanvas.height = Math.max(1, Math.round(r.height * dpr));
    this.sceneDirty = true;
    this.invalidate();
  }

  private ensureItems(): void {
    if (!this.scene) {
      this.items = [];
      return;
    }
    const view = this.camera.visibleRect();
    const zoomRatio = this.queryZoom ? this.camera.zoom / this.queryZoom : 0;
    const needs =
      this.sceneDirty ||
      !this.queryRect ||
      !rectContainsRect(this.queryRect, view) ||
      zoomRatio < 0.8 ||
      zoomRatio > 1.25;
    if (!needs) return;
    // superset with margins so panning reuses static GPU data
    const margin = Math.max(this.camera.width, this.camera.height) * 0.5;
    this.queryRect = this.camera.visibleRect(margin);
    this.queryZoom = this.camera.zoom;
    this.items = this.scene.visible(this.queryRect);
    this.sceneDirty = false;
  }

  private frame(t: number): void {
    this.raf = 0;
    if (this.disposed || !this.dirty) return;
    this.dirty = false;
    const t0 = performance.now();
    if (this.backend.lost) {
      this.backend.dispose();
      this.createBackend('canvas2d', 'context-lost');
      this.handleResize();
    }
    this.ensureItems();
    const input: FrameInput = {
      camera: this.camera.state(),
      items: this.items,
      background: this.opts.background,
      grid: this.opts.grid,
      images: this.opts.images,
      textLodPx: this.opts.textLodPx,
      darkInk: this.opts.darkInk,
    };
    try {
      this.backend.render(input);
    } catch (e) {
      if (this.backend.kind === 'webgl2') {
        this.backend.dispose();
        this.createBackend('canvas2d', 'render-error: ' + (e instanceof Error ? e.message : String(e)));
        this.handleResize();
        this.backend.render(input);
      } else throw e;
    }
    // overlay in screen space
    const octx = this.overlayCtx;
    octx.setTransform(1, 0, 0, 1, 0, 0);
    octx.clearRect(0, 0, this.overlayCanvas.width, this.overlayCanvas.height);
    if (this.overlayDraw) {
      octx.setTransform(this.camera.dpr, 0, 0, this.camera.dpr, 0, 0);
      this.overlayDraw(octx, this.camera);
    }
    const t1 = performance.now();
    this.frameMs = t1 - t0;
    this.frameMsEma = this.frameMsEma ? this.frameMsEma * 0.9 + this.frameMs * 0.1 : this.frameMs;
    if (this.lastFrameAt && t - this.lastFrameAt < 250) {
      const inst = 1000 / Math.max(1, t - this.lastFrameAt);
      this.fpsEma = this.fpsEma ? this.fpsEma * 0.9 + inst * 0.1 : inst;
    }
    this.lastFrameAt = t;
    this.frames++;
    const st = this.backend.stats();
    if (st.pending > 0) this.invalidate();
    if (this.statsListeners.size) {
      const s = this.stats();
      for (const fn of this.statsListeners) fn(s);
    }
  }

  stats(): RendererStats {
    const st = this.backend.stats();
    return {
      backend: this.backendKind,
      fps: this.fpsEma,
      frameMs: this.frameMs,
      frameMsAvg: this.frameMsEma,
      visible: this.items.length,
      drawCalls: st.drawCalls,
      textures: st.textures,
      textureBytes: st.textureBytes,
      pending: st.pending,
      frames: this.frames,
    };
  }

  /** Render synchronously now (tests / export / benchmarks). */
  renderNow(): void {
    this.dirty = true;
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
    this.frame(performance.now());
  }

  dispose(): void {
    this.disposed = true;
    if (this.raf) cancelAnimationFrame(this.raf);
    this.ro.disconnect();
    this.unsubScene?.();
    this.backend.dispose();
    this.mainCanvas.remove();
    this.overlayCanvas.remove();
    this.statsListeners.clear();
  }
}
